<template>
  <div ref="hubspotRef" class="register">
    <LayoutTitle>
      <template #title>
        <span v-if="awsMarketplaceToken">Create your account</span>
        <span v-else>SLOs made simple</span>
      </template>
      <template #subtitle>
        <span v-if="stagedLicense.isAvailable.value">
          Complete the registration to start your trial. The trial ends on
          {{ stagedLicense.expirationDateFormatted.value }}.
        </span>
        <span v-else-if="tier.isTeams">Start now with your SLO adventure!</span>
        <span v-else-if="awsMarketplaceToken">
          Thank you for purchasing the Nobl9 SLO Platform. To activate your
          subscription, provide all the information in the registration form.
          Note that the purchase is not complete until this form is submitted.
        </span>
        <span v-else>Start your free trial</span>
        <span v-if="tier.isTeams" class="register__subtitle-secondary">
          Nobl9 will contact you shortly to upgrade your account to Teams
          edition.
        </span>
      </template>
    </LayoutTitle>

    <ElForm
      class="register__form"
      :class="{
        'register__form--staged-license': stagedLicense.isAvailable.value,
      }"
      ref="form"
      @submit="submitForm"
      v-loading="isLoading"
      novalidate
    >
      <ElFormItem>
        <ElInput
          placeholder="First name"
          type="text"
          v-model="formModel.firstName"
          @change="vuelidate.firstName.$touch"
          :class="{ 'input-error': vuelidate.firstName.$error }"
        ></ElInput>
        <ValidationErrors :errors="vuelidate.firstName.$errors" />
      </ElFormItem>
      <ElFormItem>
        <ElInput
          placeholder="Last name"
          type="text"
          v-model="formModel.lastName"
          @change="vuelidate.lastName.$touch"
          :class="{ 'input-error': vuelidate.lastName.$error }"
        ></ElInput>
        <ValidationErrors :errors="vuelidate.lastName.$errors" />
      </ElFormItem>
      <ElFormItem>
        <ElInput
          placeholder="Work email address"
          type="email"
          v-model="formModel.email"
          @change="vuelidate.email.$touch"
          :class="{ 'input-error': vuelidate.email.$error }"
          @keyup="
            () => {
              extractAndSetOrganisationName();
              emailAlreadyExistError = false;
              emailDomainNotAllowedError = false;
            }
          "
        ></ElInput>
        <ValidationErrors :errors="vuelidate.email.$errors" />
      </ElFormItem>
      <ElFormItem class="register__organization" :label="organizationLabel">
        <ElInput
          placeholder="Organization name"
          type="text"
          v-model.lazy="formModel.organization"
          @change="vuelidate.organization.$touch"
          :class="{ 'input-error': vuelidate.organization.$error }"
        ></ElInput>
        <ValidationErrors :errors="vuelidate.organization.$errors" />
      </ElFormItem>

      <ElFormItem>
        <PhoneNumber
          placeholder="Phone number"
          v-model="formModel.phoneNumber"
          @change="vuelidate.phoneNumber.$touch"
          @valid="phoneNumberValid = $event"
          @fullNumber="formModel.phoneNumberFull = $event"
        />
        <ValidationErrors
          :errors="vuelidate.phoneNumber.$errors"
          :single="true"
        />
      </ElFormItem>

      <div class="form-actions" v-if="!awsMarketplaceToken">
        <ElFormItem>
          <el-checkbox
            v-model="formModel.tosAgreement"
            :class="{ 'input-error': vuelidate.tosAgreement.$error }"
            label="tos"
            type="success"
          >
            I agree to the
            <a :href="configService.tosUrl" target="_blank">Terms of Use</a> and
            <a :href="configService.privacyPolicyUrl" target="_blank"
              >Privacy Policy</a
            >
          </el-checkbox>
          <ValidationErrors :errors="vuelidate.tosAgreement.$errors" />
        </ElFormItem>
      </div>
      <ElFormItem class="register__sign-up">
        <ElButton native-type="submit" :disabled="vuelidate.$error">
          <span v-if="awsMarketplaceToken">Create</span>
          <span v-else>Sign up</span>
        </ElButton>
        <ErrorLabel
          class="general-error"
          :error="generalError"
          v-if="generalError"
        />
      </ElFormItem>
    </ElForm>
    <div class="register__widget-list">
      <SideWidget class="message__contact" v-if="awsMarketplaceToken">
        <template #label>Need help?</template>
        <template #action>
          <a :href="configService.supportUrl">Contact us</a>
        </template>
      </SideWidget>
      <SideWidget
        v-else
        label="Already using Nobl9?"
        class="register__widget-list--sm-align-left"
      >
        <template #action>
          <a :href="configService.signInUrl">Sign in</a>
        </template>
      </SideWidget>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

import { configService } from "@/services/config-service";
import { onboardingService } from "@/services/onboarding/onboarding-service";
import { StoreActions } from "@/store";
import { RoutesName } from "@/router";
import { Tier, TierName } from "@/services/onboarding/model/tierName";
import { UserPayload } from "@/services/onboarding/model/userPayload";
import { ValidationErrorMessageCode } from "@/services/http/httpErrors";

import { setupHubspot } from "@/services/hubspot/setup";
import useVuelidate, { ValidatorFn } from "@vuelidate/core";
import { isOrganisationExcluded } from "@/modules/forms/services/organisation-exception-list";
import {
  n9Email,
  n9maxLengthWithMsg,
  n9Required,
} from "@/services/validators/common-validators";
import { helpers, sameAs } from "@vuelidate/validators";
import LayoutTitle from "@/modules/ui/LayoutTitle.vue";
import ValidationErrors from "@/modules/ui/ValidationErrors.vue";
import PhoneNumber from "@/modules/ui/PhoneNumber.vue";
import ErrorLabel from "@/modules/ui/ErrorLabel.vue";
import SideWidget from "@/modules/ui/SideWidget.vue";
import { useStagedLicense } from "@/modules/forms/composables/staged-license";

const ERROR_MSG = {
  generalError: `We couldn't finalize your sign up process. Click the Sign up button again. If the issue persists, <a href="mailto:support@nobl9.com">contact Nobl9 support</a>.`,
  emailExist: `This email address already exists, <a href="${configService.signInUrl}">sign in</a> or contact Nobl9 support.`,
  domainNotAllowed: `Please use your work email to sign up. For assistance, you can <a href="mailto:support@nobl9.com">contact support</a>.`,
  tosAgreement: "Please accept the Terms of Use and Privacy Policy",
  invalidPhoneNumber: "Invalid phone number",
};

const router = useRouter();
const route = useRoute();
const store = useStore();
const stagedLicense = useStagedLicense(route.query?.stagedLicense as string);

if (stagedLicense.isAvailable.value && !stagedLicense.isValid.value) {
  router.push(RoutesName.STAGED_LICENSE_NOT_FOUND);
}

const formModel = ref({
  firstName: "",
  lastName: "",
  email: "",
  organization: stagedLicense.organizationName.value || "",
  tosAgreement: false,
  phoneNumber: "",
  phoneNumberFull: "",
});

const emailExistValidator = () => {
  return !emailAlreadyExistError.value;
};

const emailDomainAllowedValidator = () => {
  return !emailDomainNotAllowedError.value;
};

const phoneNumberValidator = (value: string) => {
  return value === "" || (phoneNumberValid.value && !value.match(/[A-Za-z]/g));
};

const validationRules = computed(() => ({
  firstName: {
    required: n9Required("First name"),
    maxLength: n9maxLengthWithMsg(),
  },
  lastName: {
    required: n9Required("Last name"),
    maxLength: n9maxLengthWithMsg(),
  },
  email: {
    required: n9Required("Email"),
    n9Email,
    alreadyExists: helpers.withMessage(
      ERROR_MSG.emailExist,
      emailExistValidator as ValidatorFn
    ),
    domainNotAllowed: helpers.withMessage(
      ERROR_MSG.domainNotAllowed,
      emailDomainAllowedValidator as ValidatorFn
    ),
  },
  organization: {
    required: n9Required("Organization"),
    maxLength: n9maxLengthWithMsg(100),
  },
  tosAgreement: {
    sameAs: helpers.withMessage(ERROR_MSG.tosAgreement, sameAs(true)),
  },
  phoneNumber: {
    phoneNumberValidator: helpers.withMessage(
      ERROR_MSG.invalidPhoneNumber,
      phoneNumberValidator as ValidatorFn
    ),
  },
}));

const awsMarketplaceToken = ref(route.query?.awsMarketplaceToken as string);
const tier = ref(new Tier(route.query?.tier as TierName));
const generalError = ref<string | null>(null);
const emailAlreadyExistError = ref(false);
const emailDomainNotAllowedError = ref(false);
const hubspotRef = ref<HTMLElement>();
const isLoading = ref(false);
const phoneNumberValid = ref(false);

const vuelidate = useVuelidate(validationRules, formModel);

const organizationLabel = computed(() => {
  return stagedLicense.isAvailable.value ? "Organization name" : "";
});

const raiseGeneralError = () => {
  generalError.value = ERROR_MSG.generalError;
};

const goToTokenExpiredError = () => {
  router.push(RoutesName.MARKETPLACE_TOKEN_EXPIRED);
};

const goToStagedTokenExpiredError = () => {
  router.push(RoutesName.STAGED_TOKEN_EXPIRED_ERROR);
};

const goToStagedLicenseNotFoundError = () => {
  router.push(RoutesName.STAGED_LICENSE_NOT_FOUND);
};

const goToMarketPlaceAccountProvisionedError = () => {
  router.push(RoutesName.MARKETPLACE_ORGANIZATION_EXISTS);
};

const goToMarketPlaceAccountNotProvisionedError = () => {
  router.push(RoutesName.MARKETPLACE_ACCOUNT_NOT_ACTIVATED);
};

const goToEmailSent = () => {
  router.push({ name: RoutesName.EMAIL_SENT });
};

const resetGeneralError = () => {
  generalError.value = null;
};

const extractAndSetOrganisationName = () => {
  const regexp = /@(.*?)\./;
  const organisationMatch = formModel.value.email.match(regexp);
  const matchedOrganisation = organisationMatch?.[1];

  if (vuelidate.value.organization.$dirty || stagedLicense.isAvailable.value) {
    return;
  }

  if (
    !matchedOrganisation ||
    formModel.value.email === "" ||
    isOrganisationExcluded(matchedOrganisation)
  ) {
    formModel.value.organization = "";
    return;
  }

  formModel.value.organization = matchedOrganisation ?? "";
};

const validateResult = async (result: Response) => {
  switch (result.status) {
    case 400: {
      const responseData = await result.json();
      if (
        responseData?.error?.code ===
        ValidationErrorMessageCode.AWS_TOKEN_EXPIRED
      ) {
        goToTokenExpiredError();
        return false;
      }
      raiseGeneralError();
      return false;
    }
    case 404: {
      // Staged license not found
      goToStagedLicenseNotFoundError();
      return false;
    }
    case 410: {
      // Staged license expired or used
      goToStagedTokenExpiredError();
      return false;
    }
    case 409: {
      const responseData = await result.json();

      switch (responseData.error.code) {
        case ValidationErrorMessageCode.MARKETPLACE_ACCOUNT_PROVISIONED:
          goToMarketPlaceAccountProvisionedError();
          break;
        case ValidationErrorMessageCode.MARKETPLACE_ACCOUNT_NOT_PROVISIONED:
          goToMarketPlaceAccountNotProvisionedError();
          break;
        default:
          emailAlreadyExistError.value = true;
          return false;
      }

      emailAlreadyExistError.value = true;
      return false;
    }
    case 422: {
      emailDomainNotAllowedError.value = true;
      return false;
    }
    case 500:
    case 503: {
      raiseGeneralError();
      return false;
    }
    default: {
      return true;
    }
  }
};

const saveProspectToStore = (payload: UserPayload) => {
  store.dispatch(StoreActions.setProspect, payload);
};

const submitForm = async (e: Event) => {
  e.preventDefault();
  resetGeneralError();

  emailAlreadyExistError.value = false;
  emailDomainNotAllowedError.value = false;
  vuelidate.value.$touch();

  if (vuelidate.value.$error) return;

  const payload = new UserPayload({
    tosAgreement: formModel.value.tosAgreement,
    firstName: formModel.value.firstName,
    lastName: formModel.value.lastName,
    email: formModel.value.email,
    organization: formModel.value.organization,
    phoneNumber: formModel.value.phoneNumberFull,
    tier: tier.value.tierName,
    awsMarketplaceToken: awsMarketplaceToken.value,
    stagedLicenseToken: stagedLicense.token.value,
  });

  let result;
  try {
    isLoading.value = true;
    result = await onboardingService.submitProspect(payload.getPayload());
  } catch {
    raiseGeneralError();
    return;
  } finally {
    isLoading.value = false;
  }

  if (!(await validateResult(result))) {
    return;
  }

  await setupHubspot(hubspotRef.value);

  saveProspectToStore(payload);
  goToEmailSent();
};
</script>

<style lang="scss" scoped>
.register {
  &__form {
    margin-top: 32px;
    display: flex;
    flex-direction: column;

    &--staged-license {
      .register__organization {
        order: -1;
        display: flex;
        flex-direction: column;

        :deep(.el-form-item__label) {
          line-height: 22px;
          height: 22px;
          font-size: 12px;
          justify-content: flex-start;
        }
      }
    }
  }

  &__subtitle-secondary {
    display: block;
    font-size: 14px;
    line-height: 26px;
  }

  &__widget-list {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: left;
    gap: 20px;
    margin-bottom: 28px;

    .el-divider {
      height: 25px;
    }

    @media (max-width: $breakpoint) {
      gap: 10px;
      margin-bottom: 16px;
      margin-top: 16px;
      justify-content: center;
      text-align: center;

      &--sm-align-right {
        align-items: flex-start;
      }
    }
  }

  .checkbox-group {
    display: flex;
    flex-direction: column;
  }

  .el-checkbox {
    display: flex;
    white-space: normal;
    margin-bottom: 5px;
    align-items: center;
  }

  .form-actions {
    margin-top: 25px;
  }

  &__sign-up.el-form-item {
    margin: 38px 0 28px;
  }
}
</style>
